import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLang } from '../../hooks/useLang';
import universitiesData from '../../data/universities.json';
import schoolsData from '../../data/schools.json';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/auth/Onboarding.scss';
const TagSelector = ({ predefinedTags, selectedTags, setSelectedTags, placeholder }) => {
    const [isAddingCustom, setIsAddingCustom] = useState(false);
    const [customVal, setCustomVal] = useState('');

    const toggleTag = (tag) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const handleAddCustom = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const val = customVal.trim();
            if (val && !selectedTags.includes(val)) {
                setSelectedTags([...selectedTags, val]);
            }
            setCustomVal('');
            setIsAddingCustom(false);
        }
    };

    const removeTag = (tag) => {
        setSelectedTags(selectedTags.filter(t => t !== tag));
    };

    return (
        <div className="predefined-tags">
            {predefinedTags.map(tag => (
                <button
                    key={tag}
                    type="button"
                    className={selectedTags.includes(tag) ? 'selected-tag' : ''}
                    onClick={() => toggleTag(tag)}
                >
                    {tag}
                </button>
            ))}

            {selectedTags.filter(t => !predefinedTags.includes(t)).map(tag => (
                <button
                    key={tag}
                    type="button"
                    className="selected-tag"
                    onClick={() => removeTag(tag)}
                >
                    {tag} &times;
                </button>
            ))}

            {!isAddingCustom ? (
                <button type="button" className="add-tag-btn" onClick={() => setIsAddingCustom(true)}>
                    + Add Custom
                </button>
            ) : (
                <input
                    type="text"
                    placeholder={placeholder}
                    value={customVal}
                    onChange={(e) => setCustomVal(e.target.value)}
                    onKeyDown={handleAddCustom}
                    onBlur={() => setIsAddingCustom(false)}
                    autoFocus
                    className="inline-custom-input"
                />
            )}
        </div>
    );
};

const Onboarding = () => {
    const navigate = useNavigate();
    const { urlLanguage } = useLang();
    const { user, token, dispatch } = useAuthContext();

    const [status, setStatus] = useState('student');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Shared Fields
    const [hobbies, setHobbies] = useState([]);
    const [birthday, setBirthday] = useState(null);
    const [location, setLocation] = useState('');
    const [allowOrgContact, setAllowOrgContact] = useState(false);

    // Student and Teacher specific (Shared Array)
    const [educations, setEducations] = useState([]);
    const [currentEduLevel, setCurrentEduLevel] = useState('Bachelor');
    const [currentInstitution, setCurrentInstitution] = useState('');
    const [customInstitution, setCustomInstitution] = useState('');
    const [currentMajor, setCurrentMajor] = useState('');
    const [customMajor, setCustomMajor] = useState('');
    const [currentYear, setCurrentYear] = useState('');
    const [availablePrograms, setAvailablePrograms] = useState([]);

    // Teacher specific
    const [teachingExperiences, setTeachingExperiences] = useState([]);
    const [currentWorkplace, setCurrentWorkplace] = useState('');
    const [currentSubject, setCurrentSubject] = useState('');
    const [currentSubjectLevel, setCurrentSubjectLevel] = useState('Expert');
    const [availableForTutoring, setAvailableForTutoring] = useState(false);

    // Company specific
    const [companyType, setCompanyType] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [website, setWebsite] = useState('');
    const [orgDescription, setOrgDescription] = useState('');
    const [offerings, setOfferings] = useState([]);
    const [contactPerson, setContactPerson] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [allowAnalytics, setAllowAnalytics] = useState(false);

    // Parent specific
    const [childrenGrades, setChildrenGrades] = useState([]);
    const [parentInvolvement, setParentInvolvement] = useState('');

    // Other specific
    const [learningGoals, setLearningGoals] = useState([]);
    const [preferredLearningStyle, setPreferredLearningStyle] = useState('');

    // Update programs when institution changes
    useEffect(() => {
        if (currentInstitution) {
            let matches = [];
            if (['Middle School', 'High School', 'Lyceum'].includes(currentEduLevel)) {
                matches = schoolsData.find(s => s.school === currentInstitution)?.programs || [];
            } else {
                matches = universitiesData.find(u => u.university === currentInstitution)?.programs || [];
            }
            setAvailablePrograms(matches);
        } else {
            setAvailablePrograms([]);
        }
    }, [currentInstitution, currentEduLevel]);

    // Check token validity on mount to prevent deadlocks from expired tokens
    useEffect(() => {
        const checkToken = async () => {
            if (!token) return;
            try {
                const response = await fetch('/api/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    if (response.status === 401) {
                        console.warn('Onboarding session invalid or expired, logging out.');
                        localStorage.removeItem('user');
                        dispatch({ type: 'LOGOUT' });
                        navigate(urlLanguage('/login'));
                    }
                }
            } catch (err) {
                console.error('Failed to verify token:', err);
            }
        };
        checkToken();
    }, [token, dispatch, navigate, urlLanguage]);

    const handleAddEducation = () => {
        const instVal = currentInstitution === 'other' ? customInstitution : currentInstitution;
        const majorVal = (currentInstitution === 'other' || currentMajor === 'other') ? (customMajor || currentMajor) : currentMajor;

        if (instVal) {
            setEducations([...educations, {
                level: currentEduLevel,
                institution: instVal,
                major: majorVal,
                year: currentYear
            }]);
            setCurrentInstitution('');
            setCustomInstitution('');
            setCurrentMajor('');
            setCustomMajor('');
            setCurrentYear('');
        }
    };

    const handleRemoveEducation = (index) => {
        setEducations(educations.filter((_, i) => i !== index));
    };

    const handleAddTeachingExperience = () => {
        if (currentWorkplace && currentSubject) {
            setTeachingExperiences([...teachingExperiences, {
                workplace: currentWorkplace,
                subject: currentSubject,
                level: currentSubjectLevel
            }]);
            setCurrentWorkplace('');
            setCurrentSubject('');
        }
    };

    const handleRemoveTeachingExperience = (index) => {
        setTeachingExperiences(teachingExperiences.filter((_, i) => i !== index));
    };

    // Obsolete single-subject logic removed as we use TeachingExperience array now

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        if (!user || (!user.email && !user.user?.email)) {
            setError('No authenticated user found for onboarding.');
            setIsLoading(false);
            return;
        }

        const _email = user.email || user.user?.email;
        const userId = user.id || user.user?.id;

        // If the user filled in an education entry but never clicked "+ Add Education",
        // include it anyway instead of rejecting the submission as if nothing was entered.
        const pendingInstitution = currentInstitution === 'other' ? customInstitution : currentInstitution;
        const pendingMajor = (currentInstitution === 'other' || currentMajor === 'other') ? (customMajor || currentMajor) : currentMajor;
        const effectiveEducations = pendingInstitution
            ? [...educations, { level: currentEduLevel, institution: pendingInstitution, major: pendingMajor, year: currentYear }]
            : educations;

        // --- Frontend Validation ---
        if (status !== 'company') {
            if (!hobbies || hobbies.length === 0) {
                setError('Please select at least one hobby or interest.');
                setIsLoading(false);
                return;
            }

            // Required fields for all non-company users
            if (!birthday) {
                setError('Birthday is required.');
                setIsLoading(false);
                return;
            }

            if (!location || location.trim() === '') {
                setError('Location is required.');
                setIsLoading(false);
                return;
            }
        }

        // Status-specific validation
        if (status === 'student') {
            if (!effectiveEducations || effectiveEducations.length === 0) {
                setError('Please add at least one education entry.');
                setIsLoading(false);
                return;
            }
        } else if (status === 'teacher') {
            if (!effectiveEducations || effectiveEducations.length === 0) {
                setError('Please add at least one education entry.');
                setIsLoading(false);
                return;
            }

            const today = new Date();
            let age = today.getFullYear() - birthday.getFullYear();
            const m = today.getMonth() - birthday.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
                age--;
            }

            if (age < 18) {
                setError('You must be at least 18 years old to register as a Teacher on YeniDers.');
                setIsLoading(false);
                return;
            }
        } else if (status === 'parent') {
            if (!childrenGrades || childrenGrades.length === 0 || childrenGrades.every(grade => !grade)) {
                setError('Please add at least one child with their education level.');
                setIsLoading(false);
                return;
            }

            const today = new Date();
            let age = today.getFullYear() - birthday.getFullYear();
            const m = today.getMonth() - birthday.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
                age--;
            }

            if (age < 18) {
                setError('You must be at least 18 years old to register as a Parent.');
                setIsLoading(false);
                return;
            }
        } else if (status === 'company') {
            if (!companyType || !companyName || !orgDescription) {
                setError('Please fill in all company information fields.');
                setIsLoading(false);
                return;
            }
        } else if (status === 'other') {
            if (!learningGoals || learningGoals.length === 0) {
                setError('Please select at least one learning goal.');
                setIsLoading(false);
                return;
            }
        }
        // ---------------------------

        // Build base payload
        let payload = {
            id: userId,
            status,
            birthday: birthday ? birthday.toISOString().split("T")[0] : null,
            location,
            hobbies,
            allow_org_contact: allowOrgContact
        };

        // Append role-specific payload
        if (status === 'student') {
            payload = { ...payload, educations: effectiveEducations };
        } else if (status === 'teacher') {
            payload = { ...payload, educations: effectiveEducations, teaching_experiences: teachingExperiences, available_for_tutoring: availableForTutoring };
        } else if (status === 'company') {
            // Company overrides bio with orgDescription
            payload = { ...payload, bio: orgDescription, company_type: companyType, company_name: companyName, website, offerings, contact_person: contactPerson, contact_email: contactEmail, allow_analytics: allowAnalytics };
            delete payload.orgDescription; // since we mapped it to bio, but just in case
        } else if (status === 'parent') {
            payload = { ...payload, children_grades: childrenGrades, parent_involvement_level: parentInvolvement, learning_goals: learningGoals, preferred_learning_style: preferredLearningStyle };
        } else if (status === 'other') {
            payload = { ...payload, learning_goals: learningGoals, preferred_learning_style: preferredLearningStyle };
        }

        try {
            const response = await fetch('/api/auth/update', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload),
            });

            const json = await response.json();

            if (response.ok) {
                console.log('Onboarding response:', json);
                console.log('User data being dispatched:', json.user);
                dispatch({ type: 'UPDATE_USER', payload: json.user });
                navigate(urlLanguage('/'));
            } else {
                console.error('Onboarding error:', json);
                if (response.status === 401 || (json.error && (json.error.toLowerCase().includes('token') || json.error.toLowerCase().includes('auth')))) {
                    console.warn('Session expired during submission, logging out.');
                    localStorage.removeItem('user');
                    dispatch({ type: 'LOGOUT' });
                    navigate(urlLanguage('/login'));
                } else {
                    setError(json.error || 'Failed to finish onboarding.');
                }
            }
        } catch (err) {
            console.error('Failed to submit onboarding data:', err);
            setError('Network error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="onboarding-page">
            <div className="onboarding-container">
                <div className="page-header">
                    <h1>More about you</h1>
                    <p>Tell us a little more so we can personalize your experience. You can change these later from your profile.</p>
                </div>

                <form onSubmit={handleSubmit} className="onboarding-form">
                    {error && <div className="error-banner">{error}</div>}

                    {/* STATUS SELECTOR */}
                    <div className="form-section">
                        <label className="section-label">Your status</label>
                        <div className="status-selector">
                            <button type="button" className={`status-pill ${status === 'student' ? 'active' : ''}`} onClick={() => setStatus('student')}>
                                Student
                            </button>
                            <button type="button" className={`status-pill ${status === 'teacher' ? 'active' : ''}`} onClick={() => setStatus('teacher')}>
                                Teacher
                            </button>
                            <button type="button" className={`status-pill ${status === 'company' ? 'active' : ''}`} onClick={() => setStatus('company')}>
                                Company
                            </button>
                            <button type="button" className={`status-pill ${status === 'parent' ? 'active' : ''}`} onClick={() => setStatus('parent')}>
                                Parent
                            </button>
                            <button type="button" className={`status-pill ${status === 'other' ? 'active' : ''}`} onClick={() => setStatus('other')}>
                                Normal User
                            </button>
                        </div>
                    </div>

                    {/* SHARED FIELDS (Hidden for company to reorganize) */}
                    {status !== 'company' && (
                        <>
                            <div className="form-section">
                                <label className="section-label">Hobbies / Interests</label>
                                <div className="tags-input-area">
                                    <TagSelector
                                        predefinedTags={['Math', 'Physics', 'Chemistry', 'Programming', 'Sports']}
                                        selectedTags={hobbies}
                                        setSelectedTags={setHobbies}
                                        placeholder="Add custom hobby (Press Enter)"
                                    />
                                    <p className="input-hint">Add a couple words about yourself (optional).</p>
                                </div>
                            </div>


                            <div className="form-section multi-input-row" style={{ marginBottom: 32 }}>
                                <div className="input-group" style={{ flex: 1 }}>
                                    <label className="section-label">Birthday</label>
                                    <DatePicker
                                        selected={birthday}
                                        onChange={(date) => setBirthday(date)}
                                        maxDate={new Date()}
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={100}
                                        placeholderText="Select your birthday..."
                                        className="custom-datepicker"
                                        dateFormat="MM/dd/yyyy"
                                    />
                                </div>
                                <div className="input-group" style={{ flex: 1 }}>
                                    <label className="section-label">Location (City, Country)</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. London, UK"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* --------- STUDENT TAB --------- */}
                    {status === 'student' && (
                        <div className="role-specific-section">
                            <h3 className="sub-heading">Education Details</h3>

                            {educations.length > 0 && (
                                <div className="added-list">
                                    {educations.map((edu, idx) => (
                                        <div key={idx} className="added-item">
                                            <div className="item-details">
                                                <strong>{edu.level}</strong> at <span>{edu.institution}</span>
                                                {edu.major && ` - ${edu.major}`}
                                                {edu.year && ` (${edu.year})`}
                                            </div>
                                            <button type="button" onClick={() => handleRemoveEducation(idx)} className="remove-btn">&times;</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="add-item-block">
                                <div className="block-header">Add New Education</div>
                                <div className="multi-input-row" style={{ marginBottom: '16px' }}>
                                    <div className="input-group" style={{ flex: 1 }}>
                                        <label>Education Level</label>
                                        <select value={currentEduLevel} onChange={(e) => {
                                            setCurrentEduLevel(e.target.value);
                                            setCurrentInstitution('');
                                            setAvailablePrograms([]);
                                        }}>
                                            <option value="Middle School">Middle School</option>
                                            <option value="High School">Lyceum</option>
                                            <option value="Associate">Associate Degree</option>
                                            <option value="Bachelor">Bachelor</option>
                                            <option value="Master">Master</option>
                                            <option value="PhD">PhD</option>
                                            <option value="College">College</option>
                                        </select>
                                    </div>
                                    <div className="input-group" style={{ flex: 2 }}>
                                        <label>Institution / School</label>
                                        <select value={currentInstitution} onChange={(e) => setCurrentInstitution(e.target.value)}>
                                            <option value="">Select Institution...</option>
                                            {['Middle School', 'High School', 'Lyceum'].includes(currentEduLevel) ? (
                                                schoolsData.map((s, idx) => (
                                                    <option key={`s-${idx}`} value={s.school}>{s.school}</option>
                                                ))
                                            ) : (
                                                universitiesData.map((u, idx) => (
                                                    <option key={`u-${idx}`} value={u.university}>{u.university}</option>
                                                ))
                                            )}
                                            <option value="other">Other / Not Listed</option>
                                        </select>
                                        {currentInstitution === 'other' && (
                                            <input
                                                type="text"
                                                placeholder="Type institution name..."
                                                value={customInstitution}
                                                onChange={(e) => setCustomInstitution(e.target.value)}
                                                style={{ marginTop: '8px' }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="multi-input-row">
                                    <div className="input-group" style={{ flex: 2 }}>
                                        <label>{['Middle School', 'High School', 'Lyceum'].includes(currentEduLevel) ? 'Grade / Program' : 'Major / Specialization'}</label>
                                        {currentInstitution === 'other' ? (
                                            <input
                                                type="text"
                                                placeholder="Type major/program..."
                                                value={customMajor}
                                                onChange={(e) => setCustomMajor(e.target.value)}
                                            />
                                        ) : (
                                            <>
                                                <select value={currentMajor} onChange={(e) => setCurrentMajor(e.target.value)} disabled={!currentInstitution}>
                                                    <option value="">Select...</option>
                                                    {availablePrograms.map((prog, idx) => (
                                                        <option key={idx} value={prog}>{prog}</option>
                                                    ))}
                                                    <option value="other">Other / Not Listed</option>
                                                </select>
                                                {currentMajor === 'other' && (
                                                    <input
                                                        type="text"
                                                        placeholder="Type major/program..."
                                                        value={customMajor}
                                                        onChange={(e) => setCustomMajor(e.target.value)}
                                                        style={{ marginTop: '8px' }}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="input-group" style={{ flex: 1 }}>
                                        <label>Year (optional)</label>
                                        <select value={currentYear} onChange={(e) => setCurrentYear(e.target.value)}>
                                            <option value="">Select Year...</option>
                                            {['Middle School', 'High School', 'Lyceum'].includes(currentEduLevel) ? (
                                                <>
                                                    <option value="Year 1">Year 1</option>
                                                    <option value="Year 2">Year 2</option>
                                                    <option value="Year 3">Year 3</option>
                                                    <option value="Year 4">Year 4</option>
                                                    <option value="Year 5">Year 5</option>
                                                </>
                                            ) : (
                                                <>
                                                    <option value="Year 1">Year 1</option>
                                                    <option value="Year 2">Year 2</option>
                                                    <option value="Year 3">Year 3</option>
                                                    <option value="Year 4">Year 4</option>
                                                    <option value="Masters">Masters</option>
                                                </>
                                            )}
                                        </select>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="inline-add-btn-large"
                                    onClick={handleAddEducation}
                                    disabled={!currentInstitution || (currentInstitution === 'other' && !customInstitution.trim())}
                                >
                                    + Add Education
                                </button>
                            </div>

                        </div>
                    )}

                    {/* --------- TEACHER TAB --------- */}
                    {status === 'teacher' && (
                        <div className="role-specific-section">
                            <h3 className="sub-heading">Teacher Profile</h3>

                            <div className="input-group" style={{ marginBottom: "32px" }}>
                                <label>Your Education Background</label>
                                {educations.length > 0 && (
                                    <div className="added-list">
                                        {educations.map((edu, idx) => (
                                            <div key={idx} className="added-item">
                                                <div className="item-details">
                                                    <strong>{edu.level}</strong> at <span>{edu.institution}</span>
                                                    {edu.major && ` - ${edu.major}`}
                                                </div>
                                                <button type="button" onClick={() => handleRemoveEducation(idx)} className="remove-btn">&times;</button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="add-item-block" style={{ marginBottom: 0 }}>
                                    <div className="block-header">Add Educational Degree</div>
                                    <div className="multi-input-row" style={{ marginBottom: '16px' }}>
                                        <div className="input-group" style={{ flex: 1 }}>
                                            <label>Degree</label>
                                            <select value={currentEduLevel} onChange={(e) => {
                                                setCurrentEduLevel(e.target.value);
                                                setCurrentInstitution('');
                                                setAvailablePrograms([]);
                                            }}>
                                                <option value="Bachelor">Bachelor</option>
                                                <option value="Master">Master</option>
                                                <option value="PhD">PhD</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div className="input-group" style={{ flex: 2 }}>
                                            <label>Institution Name</label>
                                            <select value={currentInstitution} onChange={(e) => setCurrentInstitution(e.target.value)}>
                                                <option value="">Select Institution...</option>
                                                {universitiesData.map((u, idx) => (
                                                    <option key={`u-${idx}`} value={u.university}>{u.university}</option>
                                                ))}
                                                <option value="other">Other / Not Listed</option>
                                            </select>
                                            {currentInstitution === 'other' && (
                                                <input
                                                    type="text"
                                                    placeholder="Type institution name..."
                                                    value={customInstitution}
                                                    onChange={(e) => setCustomInstitution(e.target.value)}
                                                    style={{ marginTop: '8px' }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="input-group">
                                        <label>Major / Field of Study (optional)</label>
                                        <input type="text" placeholder="e.g. Computer Science" value={currentMajor} onChange={(e) => setCurrentMajor(e.target.value)} />
                                    </div>
                                    <button
                                        type="button"
                                        className="inline-add-btn-large"
                                        onClick={handleAddEducation}
                                        disabled={!currentInstitution || (currentInstitution === 'other' && !customInstitution.trim())}
                                    >
                                        + Add Education
                                    </button>
                                </div>
                            </div>

                            <div className="input-group subjects-group" style={{ marginBottom: "32px" }}>
                                <label>Teaching Experience / Subjects Taught</label>
                                {teachingExperiences.length > 0 && (
                                    <div className="added-list">
                                        {teachingExperiences.map((exp, idx) => (
                                            <div key={idx} className="added-item experience-item">
                                                <div className="item-details">
                                                    <div className="exp-subject">
                                                        <strong>{exp.subject}</strong>
                                                        <span className={`level-badge level-${exp.level.toLowerCase()}`}>{exp.level}</span>
                                                    </div>
                                                    <div className="exp-workplace">
                                                        at <span>{exp.workplace}</span>
                                                    </div>
                                                </div>
                                                <button type="button" onClick={() => handleRemoveTeachingExperience(idx)} className="remove-btn">&times;</button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="add-item-block" style={{ marginBottom: 0 }}>
                                    <div className="block-header">Add Teaching Experience</div>
                                    <div className="input-group">
                                        <label>Workplace / School name</label>
                                        <input type="text" placeholder="e.g. YeniDers Academy" value={currentWorkplace} onChange={(e) => setCurrentWorkplace(e.target.value)} />
                                    </div>
                                    <div className="multi-input-row">
                                        <div className="input-group" style={{ flex: 2 }}>
                                            <label>Subject</label>
                                            <input type="text" placeholder="e.g. Mathematics" value={currentSubject} onChange={(e) => setCurrentSubject(e.target.value)} />
                                        </div>
                                        <div className="input-group" style={{ flex: 1 }}>
                                            <label>Level</label>
                                            <select value={currentSubjectLevel} onChange={(e) => setCurrentSubjectLevel(e.target.value)}>
                                                <option value="Beginner">Beginner</option>
                                                <option value="Intermediate">Intermediate</option>
                                                <option value="Expert">Expert</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="inline-add-btn-large"
                                        onClick={handleAddTeachingExperience}
                                        disabled={!currentWorkplace.trim() || !currentSubject.trim()}
                                    >
                                        + Add Experience
                                    </button>
                                </div>
                            </div>


                            <div className="toggle-group-inline">
                                <div className="toggle-text">
                                    <strong>Available for paid answers / tutoring</strong>
                                    <p>Teachers can earn by answering questions. We'll show you ways to monetize your expertise.</p>
                                </div>
                                <label className="switch">
                                    <input type="checkbox" checked={availableForTutoring} onChange={(e) => setAvailableForTutoring(e.target.checked)} />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* --------- COMPANY TAB --------- */}
                    {status === 'company' && (
                        <div className="role-specific-section">
                            <h3 className="sub-heading">Organization Details</h3>

                            <div className="input-group">
                                <label>Company type</label>
                                <select value={companyType} onChange={(e) => setCompanyType(e.target.value)}>
                                    <option value="">Select company type...</option>
                                    <option value="EdTech">EdTech</option>
                                    <option value="University">University</option>
                                    <option value="Bootcamp">Bootcamp</option>
                                    <option value="Corporate">Corporate</option>
                                    <option value="Non-Profit">Non-Profit</option>
                                </select>
                            </div>

                            <div className="input-group">
                                <label>Company name</label>
                                <input type="text" placeholder="Enter company name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                            </div>

                            <div className="input-group">
                                <label>Website (optional)</label>
                                <input type="url" placeholder="https://example.com" value={website} onChange={(e) => setWebsite(e.target.value)} />
                            </div>

                            <div className="input-group">
                                <label>Organization description (max 300 chars)</label>
                                <textarea
                                    placeholder="What does your organization do?"
                                    value={orgDescription}
                                    onChange={(e) => setOrgDescription(e.target.value)}
                                    maxLength={300}
                                />
                                <div className="char-count">{orgDescription.length}/300</div>
                            </div>

                            <div className="input-group">
                                <label>What are you offering on YeniDers?</label>
                                <div className="tags-input-area">
                                    <TagSelector
                                        predefinedTags={['Internship positions', 'Volunteer roles', 'Sponsored courses', 'Hiring']}
                                        selectedTags={offerings}
                                        setSelectedTags={setOfferings}
                                        placeholder="Add custom offering (Press Enter)"
                                    />
                                </div>
                            </div>

                            <div className="multi-input-row">
                                <div className="input-group">
                                    <label>Contact person</label>
                                    <input type="text" placeholder="Full name" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
                                </div>
                                <div className="input-group">
                                    <label>Contact email</label>
                                    <input type="email" placeholder="contact@example.com" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                                </div>
                            </div>

                            <div className="toggle-group-inline bordered-toggle">
                                <div className="toggle-text">
                                    <strong>Allow analytics for listings</strong>
                                    <p>Show anonymous view & click counts to organizers</p>
                                </div>
                                <label className="switch">
                                    <input type="checkbox" checked={allowAnalytics} onChange={(e) => setAllowAnalytics(e.target.checked)} />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* --------- PARENT TAB --------- */}
                    {status === 'parent' && (
                        <div className="role-specific-section">
                            <h3 className="sub-heading">Parent Profile</h3>

                            <div className="input-group">
                                <label>Number of Children</label>
                                <select value={childrenGrades.length > 0 ? childrenGrades.length : ''} onChange={(e) => {
                                    const num = parseInt(e.target.value);
                                    if (num === 0) {
                                        setChildrenGrades([]);
                                    } else {
                                        const currentGrades = [...childrenGrades];
                                        while (currentGrades.length < num) {
                                            currentGrades.push('');
                                        }
                                        while (currentGrades.length > num) {
                                            currentGrades.pop();
                                        }
                                        setChildrenGrades(currentGrades);
                                    }
                                }}>
                                    <option value="">Select number of children...</option>
                                    <option value="1">1 Child</option>
                                    <option value="2">2 Children</option>
                                    <option value="3">3 Children</option>
                                    <option value="4">4+ Children</option>
                                </select>
                            </div>

                            {childrenGrades.map((grade, index) => (
                                <div key={index} className="input-group">
                                    <label>Child {index + 1} - Education Level</label>
                                    <select value={grade} onChange={(e) => {
                                        const newGrades = [...childrenGrades];
                                        newGrades[index] = e.target.value;
                                        setChildrenGrades(newGrades);
                                    }}>
                                        <option value="">Select education level...</option>
                                        <option value="Preschool">Preschool</option>
                                        <option value="Elementary">Elementary School</option>
                                        <option value="Middle School">Middle School</option>
                                        <option value="High School">High School</option>
                                        <option value="College">College</option>
                                    </select>
                                </div>
                            ))}

                            <div className="input-group">
                                <label>Primary Educational Goals</label>
                                <div className="tags-input-area">
                                    <TagSelector
                                        predefinedTags={['Academic Support', 'Test Preparation', 'Enrichment Activities', 'College Planning', 'Skill Development', 'Special Education Support']}
                                        selectedTags={learningGoals}
                                        setSelectedTags={setLearningGoals}
                                        placeholder="Add custom goal (Press Enter)"
                                    />
                                </div>
                            </div>

                            <div className="input-group">
                                <label>Level of Involvement</label>
                                <select value={parentInvolvement} onChange={(e) => setParentInvolvement(e.target.value)}>
                                    <option value="">Select involvement level...</option>
                                    <option value="Highly Involved">Highly Involved (Daily homework help, tutoring coordination)</option>
                                    <option value="Regularly Involved">Regularly Involved (Weekly check-ins, progress monitoring)</option>
                                    <option value="Occasionally Involved">Occasionally Involved (Monthly reviews, report card monitoring)</option>
                                    <option value="Seeking Resources">Seeking Resources (Looking for tools and support)</option>
                                </select>
                            </div>

                            <div className="input-group">
                                <label>Preferred Communication Method</label>
                                <select value={preferredLearningStyle} onChange={(e) => setPreferredLearningStyle(e.target.value)}>
                                    <option value="">Select preference...</option>
                                    <option value="Email Updates">Email Updates</option>
                                    <option value="Mobile App">Mobile App Notifications</option>
                                    <option value="SMS Text">SMS Text Messages</option>
                                    <option value="Weekly Reports">Weekly Summary Reports</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* --------- OTHER / NORMAL USER TAB --------- */}
                    {status === 'other' && (
                        <div className="role-specific-section">
                            <h3 className="sub-heading">Learner Profile</h3>

                            <div className="input-group">
                                <label>What are your goals?</label>
                                <div className="tags-input-area">
                                    <TagSelector
                                        predefinedTags={['Career Change', 'General Knowledge', 'Skill Building']}
                                        selectedTags={learningGoals}
                                        setSelectedTags={setLearningGoals}
                                        placeholder="Add custom goal (Press Enter)"
                                    />
                                </div>
                            </div>

                            <div className="input-group">
                                <label>Preferred Learning Style</label>
                                <select value={preferredLearningStyle} onChange={(e) => setPreferredLearningStyle(e.target.value)}>
                                    <option value="">Select learning style...</option>
                                    <option value="Video Tutorials">Video Tutorials</option>
                                    <option value="Reading Materials">Reading Materials</option>
                                    <option value="Interactive Exercises">Interactive Exercises</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* PRIVACY CONTROLS (SHARED) */}
                    <div className="privacy-section">
                        <h3 className="sub-heading">Privacy preferences</h3>

                        <div className="toggle-group-inline hide-border">
                            <div className="toggle-text">
                                <strong>Allow organization to contact me</strong>
                                <p>For job/internship outreach.</p>
                            </div>
                            <label className="switch">
                                <input type="checkbox" checked={allowOrgContact} onChange={(e) => setAllowOrgContact(e.target.checked)} />
                                <span className="slider round"></span>
                            </label>
                        </div>
                        <p className="terms-text">By continuing, you agree to our <button type="button" className="terms-link">Terms &amp; Privacy Policy</button>.</p>
                    </div>

                    {/* FORM FOOTER */}
                    <div className="form-footer">
                        <p className="footer-hint">You can update these details later from your profile.</p>
                        <div className="footer-actions">
                            <button type="button" className="action-btn back-btn" onClick={() => navigate(urlLanguage('/'))}>Back</button>
                            <button type="submit" className="action-btn continue-btn" disabled={isLoading}>{isLoading ? 'Saving...' : 'Continue'}</button>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default Onboarding;
